import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
	onAuthStateChanged,
	sendPasswordResetEmail,
	signInWithEmailAndPassword,
	signOut as firebaseSignOut,
	User,
	sendEmailVerification
} from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, Timestamp, updateDoc, where } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { Agent } from '../types/agent';

interface AuthContextType {
	currentUser: User | null;
	currentAgent: Agent | null;
	loading: boolean;
	signIn: (email: string, password: string) => Promise<{ user: User; agent: Agent | null }>;
	signOut: () => Promise<void>;
	logout: () => Promise<void>;
	resetPassword: (email: string) => Promise<void>;
	sendVerificationEmail: (user?: User) => Promise<void>;
	refreshSubscriptionStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};

interface AuthProviderProps {
	children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const [currentUser, setCurrentUser] = useState<User | null>(null);
	const [currentAgent, setCurrentAgent] = useState<Agent | null>(null);
	const [loading, setLoading] = useState(true);

	const loadAgentData = useCallback(async (user: User): Promise<Agent> => {
		try {
			// Try to find agent by Firebase user ID - this aligns with firestore.rules
			const agentDoc = await getDoc(doc(db, 'agents', user.uid));
			let agentData = null;
			let agentId = user.uid; // Always use Firebase user UID as agent document ID

			if (agentDoc.exists()) {
				agentData = agentDoc.data();
			} else {
				// Legacy records whose document ID is not the Firebase UID are
				// matched on firebaseUserId, NOT on email.
				//
				// Matching by email meant any signed-in user whose address happened
				// to equal an agent's - a mobile app user, say - adopted that agent
				// document and got its role in memory, including super_agent. The
				// rules still denied the underlying data, but every client-side
				// guard passed and the admin UI rendered. firebaseUserId is a claim
				// only the account owner can satisfy.
				const agentQuery = query(
					collection(db, 'agents'),
					where('firebaseUserId', '==', user.uid)
				);

				const querySnapshot = await getDocs(agentQuery);

				if (!querySnapshot.empty) {
					const agentDocFromQuery = querySnapshot.docs[0];
					if (agentDocFromQuery) {
						agentData = agentDocFromQuery.data();
						agentId = agentDocFromQuery.id;
					}

					// Update the agent document to link it to the Firebase user ID for future logins
					try {
						await updateDoc(doc(db, 'agents', agentId), {
							firebaseUserId: user.uid, updatedAt: new Date()
						});
					} catch (linkError) {
						// Failed to link, but continue
					}
				} else {
					console.error('[AuthContext] loadAgentData: Agent document not found for user:', user.uid);
					throw new Error('No agent profile found for this account. Please contact your administrator.');
				}
			}

			if (!agentData) {
				throw new Error('Agent data is null. Please contact your administrator.');
			}

			// Validate required agent fields - be flexible with field names
			const email = agentData.email || user.email;
			const firstName = agentData.firstName || agentData.name?.split(' ')[0] || 'Agent';
			const lastName = agentData.lastName || agentData.name?.split(' ').slice(1).join(' ') || '';
			const displayName = agentData.displayName || agentData.name || `${firstName} ${lastName}`.trim();
			const companyName = agentData.companyName || agentData.company || 'Unknown Company';

			if (!email) {
				console.error('[AuthContext] loadAgentData: Agent document missing email:', agentId);
				throw new Error('Agent document is missing email. Please contact your administrator.');
			}

			// Check if agent is active
			if (agentData.isActive === false) {
				console.error('[AuthContext] loadAgentData: Agent account is disabled:', agentId);
				throw new Error('Your agent account has been disabled. Please contact your administrator.');
			}

			const agent = {
				id: agentId,
				email,
				firstName,
				lastName,
				displayName,
				companyName,
				phone: agentData.phone || '',
				role: agentData.role || 'agent',
				isActive: agentData.isActive !== false, // Default to true if not specified
				createdAt: agentData.createdAt instanceof Timestamp ? agentData.createdAt.toDate() : new Date(),
				updatedAt: agentData.updatedAt instanceof Timestamp ? agentData.updatedAt.toDate() : new Date(),
				lastLoginAt: agentData.lastLoginAt instanceof Timestamp ? agentData.lastLoginAt.toDate() : undefined,
				firebaseUserId: agentData.firebaseUserId || user.uid,
				assignedCustomers: agentData.assignedCustomers || [],
				territory: agentData.territory,
				licenseNumber: agentData.licenseNumber,
				specialties: agentData.specialties || [],
				// Subscription fields
				stripeCustomerId: agentData.stripeCustomerId,
				subscriptionStatus: agentData.subscriptionStatus || 'none',
				subscriptionRequired: agentData.role === 'agent', // Only regular agents need subscriptions
				companyLogo: agentData.companyLogo,
				profileImage: agentData.profileImage
		} as Agent;

			setCurrentAgent(agent);

			// Update last login time
			try {
				await updateDoc(doc(db, 'agents', agentId), {
					lastLoginAt: new Date()
				});
			} catch (updateError) {
				console.warn('[AuthContext] loadAgentData: Failed to update last login time:', updateError);
				// Don't throw here - login should still succeed
			}

			return agent; // Return the agent data
		} catch (error) {
			console.error('[AuthContext] loadAgentData: Error loading agent data:', error);
			setCurrentAgent(null);
			throw error; // Re-throw to handle in the auth state change handler
		}
	}, []);

	const refreshSubscriptionStatus = useCallback(async (): Promise<void> => {
		if (!currentAgent || !currentUser) {
			return;
		}

		try {
			const agentDoc = await getDoc(doc(db, 'agents', currentAgent.id));

			if (agentDoc.exists()) {
				const agentData = agentDoc.data();

				setCurrentAgent(prevAgent => {
					if (!prevAgent) return null;

					return {
						...prevAgent,
						stripeCustomerId: agentData.stripeCustomerId,
						subscriptionStatus: agentData.subscriptionStatus || 'none',
						subscriptionRequired: agentData.role === 'agent'
					};
				});
			}
		} catch (error) {
			console.error('[AuthContext] refreshSubscriptionStatus: Error refreshing subscription status:', error);
		}
	}, [currentAgent, currentUser]);

	const signIn = useCallback(async (email: string, password: string): Promise<{ user: User; agent: Agent | null }> => {
		try {
			const result = await signInWithEmailAndPassword(auth, email, password);

			// Load agent data immediately after sign in to avoid race condition
			let agent: Agent | null = null;
			try {
				// Use the agent returned directly from loadAgentData
				agent = await loadAgentData(result.user);
			} catch (agentError) {
				console.error('[AuthContext] signIn: Failed to load agent data after sign in:', agentError);
				// Sign out if agent data can't be loaded
				await firebaseSignOut(auth);
				throw agentError;
			}

			return { user: result.user, agent };
		} catch (error: any) {
			console.error('[AuthContext] signIn: Sign in error:', error.code, error.message);
			throw error;
		}
	}, [loadAgentData]);

	const signOut = useCallback(async (): Promise<void> => {
		setCurrentAgent(null);
		await firebaseSignOut(auth);
	}, []);

	const logout = useCallback(async (): Promise<void> => {
		setCurrentAgent(null);
		await firebaseSignOut(auth);
	}, []);

	const resetPassword = useCallback(async (email: string): Promise<void> => {
		await sendPasswordResetEmail(auth, email);
	}, []);

	const sendVerificationEmail = useCallback(async (user?: User): Promise<void> => {
		const userToVerify = user || currentUser;
		if (!userToVerify) throw new Error('User must be signed in to send verification email');

		try {
			await sendEmailVerification(userToVerify);
		} catch (error: any) {
			console.error('[AuthContext] sendVerificationEmail: Error sending verification email:', error);
			throw error;
		}
	}, [currentUser]);

	useEffect(() => {
		return onAuthStateChanged( auth, async( user ) => {
			// On registration page, don't interfere with RegistrationContext's flow
			// RegistrationContext handles the full registration process including activation
			const isRegistrationPage = window.location.pathname.includes('/register');
			if (isRegistrationPage) {
				setCurrentUser(user);
				setLoading(false);
				return;
			}

			if ( user ) {
				// Keep loading true while we fetch agent data
				setLoading( true );
				setCurrentUser( user );

				try {
					await loadAgentData( user );
					setLoading( false );
				} catch ( error ) {
					console.error( '[AuthContext] onAuthStateChanged: Failed to load agent data during auth state change:', error );
					// Sign out the user if agent data can't be loaded
					await firebaseSignOut( auth );
					setCurrentUser( null );
					setCurrentAgent( null );
					setLoading( false );
				}
			} else {
				setCurrentUser( null );
				setCurrentAgent( null );
				setLoading( false );
			}
		} );
	}, [loadAgentData]);

	const value: AuthContextType = useMemo(() => ({
		currentUser,
		currentAgent,
		loading,
		signIn,
		signOut,
		logout,
		resetPassword,
		sendVerificationEmail,
		refreshSubscriptionStatus
	}), [
		currentUser,
		currentAgent,
		loading,
		signIn,
		signOut,
		logout,
		resetPassword,
		sendVerificationEmail,
		refreshSubscriptionStatus
	]);

	return (<AuthContext.Provider value={value}>
		{children}
	</AuthContext.Provider>);
};
